import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PhoneCall, Mail, MapPin, ArrowRight, CheckCircle2, Star, Sparkles, Shield, Clock, Award, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { mockData } from '../data/mock';
import QuoteModal from '../components/QuoteModal';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';

const Offers = () => {
  usePageMeta('35% Off Epoxy Flooring Sydney | Special Offer | Alpha Epoxy Floors', 'Limited time 35% off epoxy flooring in Sydney. Premium epoxy flooring for garages, warehouses, commercial spaces. 15+ years experience. 500+ happy customers. Get a free quote today.', true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    suburb: '',
    message: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingButton(window.scrollY > 2200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email) {
      toast({ title: "Missing Information", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'offers-hero' })
      });
      if (!res.ok) throw new Error('Failed');
      toast({ title: "Quote Request Received!", description: "We'll contact you within 24 hours to discuss your project." });
      if (window.gtag) { window.gtag('event', 'conversion', { 'send_to': 'AW-18269270249/IPghCNXa_sQcEOnhu4dE', 'value': 1.0, 'currency': 'AUD' }); }
      setFormData({ name: '', email: '', phone: '', suburb: '', message: '' });
    } catch (err) {
      toast({ title: "Error", description: "Something went wrong. Please call us at 0451 945 455.", variant: "destructive" });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black">
      <Header activePage="offers" />

      {/* Hero Section - Matching Reference Design */}
      <section className="relative overflow-hidden mt-12" data-testid="offers-hero">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/images/hero-bg.png"
            alt="Alpha Epoxy Flooring Background"
            className="w-full h-full object-cover"
            style={{ objectPosition: '30% center' }}
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        {/* Content Grid */}
        <div className="relative z-10 w-full">
          <div className="grid lg:grid-cols-2">

            {/* Left Column - Promotional Text */}
            <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-10 lg:py-14">
              {/* Main Heading */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] font-black text-white uppercase leading-[0.95] tracking-tight mb-6" style={{ textShadow: '3px 3px 8px rgba(0,0,0,0.7)', maxWidth: '700px' }}>
                #1 EPOXY FLOORING IN SYDNEY
              </h1>

              {/* 35% OFF THIS MONTH */}
              <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                <span className="text-3xl sm:text-4xl md:text-[2.8rem] font-black text-red-600 leading-none" style={{ textShadow: '2px 2px 6px rgba(0,0,0,0.5)' }}>35% OFF</span>
                <span className="text-2xl sm:text-3xl md:text-[2.5rem] font-black text-white uppercase leading-none" style={{ textShadow: '2px 2px 6px rgba(0,0,0,0.5)' }}>THIS MONTH</span>
              </div>

              {/* SPECIAL OFFER Badge */}
              <div className="mb-6">
                <span className="inline-block bg-red-600 text-white text-xl sm:text-2xl font-black uppercase px-5 py-2 tracking-wide">
                  SPECIAL OFFER
                </span>
              </div>

              {/* Tagline */}
              <p className="text-xl sm:text-2xl text-white italic mb-6" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.6)', fontStyle: 'italic' }}>
                A Time For Renewal. Make Your Floors New Again.
              </p>

              {/* Stats Line */}
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white uppercase tracking-wide mb-10" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.6)' }}>
                15+ YEARS IN BUSINESS &middot; 500+ HAPPY CUSTOMERS
              </p>

              {/* Google Reviews Badge - Horizontal */}
              <div className="flex items-stretch w-fit rounded-lg overflow-hidden shadow-lg" data-testid="google-reviews-badge">
                {/* Red left section */}
                <div className="bg-red-600 px-4 py-2 flex items-center gap-2">
                  <span className="text-white text-xs font-semibold whitespace-nowrap">We reached</span>
                  <span className="text-white text-2xl font-black leading-none">40+</span>
                  <span className="text-white text-xs font-semibold whitespace-nowrap">5 STAR reviews</span>
                </div>
                {/* White right section with Google logo */}
                <div className="bg-white px-4 py-2 flex items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    <span style={{ color: '#4285F4', fontWeight: 700, fontSize: '1rem' }}>G</span>
                    <span style={{ color: '#EA4335', fontWeight: 700, fontSize: '1rem' }}>o</span>
                    <span style={{ color: '#FBBC05', fontWeight: 700, fontSize: '1rem' }}>o</span>
                    <span style={{ color: '#4285F4', fontWeight: 700, fontSize: '1rem' }}>g</span>
                    <span style={{ color: '#34A853', fontWeight: 700, fontSize: '1rem' }}>l</span>
                    <span style={{ color: '#EA4335', fontWeight: 700, fontSize: '1rem' }}>e</span>
                  </div>
                  <span className="text-gray-700 text-xs font-semibold">Reviews</span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form Panel (no extra overlay) */}
            <div className="flex items-center justify-center lg:justify-end py-10 lg:py-14 px-6 sm:px-10 lg:px-16">
              <div className="w-full max-w-lg">
                {/* Form Card with red accent */}
                <div className="bg-black/40 backdrop-blur-sm border border-white/20 rounded-lg p-10">
                  {/* Form Heading */}
                  <div className="mb-6 pb-5 border-b border-white/20">
                    <p className="text-red-500 text-sm font-bold uppercase tracking-widest mb-2">Epoxy Flooring</p>
                    <h2 className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight">
                      SPECIAL EPOXY<br />PAINTING <span className="text-red-600">SALE</span>
                    </h2>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="offers-form">
                    <div>
                      <label className="block text-white/90 text-xs font-semibold uppercase tracking-wider mb-1.5">Your Name *</label>
                      <input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        className="w-full h-11 px-4 bg-white text-gray-900 text-sm rounded border-0 outline-none focus:ring-2 focus:ring-red-600 placeholder:text-gray-400"
                        data-testid="offers-name-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-white/90 text-xs font-semibold uppercase tracking-wider mb-1.5">Your Email *</label>
                        <input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="email@example.com"
                          required
                          className="w-full h-11 px-4 bg-white text-gray-900 text-sm rounded border-0 outline-none focus:ring-2 focus:ring-red-600 placeholder:text-gray-400"
                          data-testid="offers-email-input"
                        />
                      </div>
                      <div>
                        <label className="block text-white/90 text-xs font-semibold uppercase tracking-wider mb-1.5">Phone *</label>
                        <input
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="04XX XXX XXX"
                          required
                          className="w-full h-11 px-4 bg-white text-gray-900 text-sm rounded border-0 outline-none focus:ring-2 focus:ring-red-600 placeholder:text-gray-400"
                          data-testid="offers-phone-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/90 text-xs font-semibold uppercase tracking-wider mb-1.5">Suburb</label>
                      <input
                        name="suburb"
                        value={formData.suburb}
                        onChange={handleChange}
                        placeholder="e.g. Bondi, Parramatta"
                        className="w-full h-11 px-4 bg-white text-gray-900 text-sm rounded border-0 outline-none focus:ring-2 focus:ring-red-600 placeholder:text-gray-400"
                        data-testid="offers-suburb-input"
                      />
                    </div>

                    <div>
                      <label className="block text-white/90 text-xs font-semibold uppercase tracking-wider mb-1.5">Your Message</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Tell us about your project..."
                        className="w-full px-4 py-3 bg-white text-gray-900 text-sm rounded border-0 outline-none resize-none focus:ring-2 focus:ring-red-600 placeholder:text-gray-400"
                        data-testid="offers-message-input"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-black text-lg uppercase py-4 px-6 rounded tracking-wider transition-all duration-200 hover:shadow-lg hover:shadow-red-600/30 mt-2"
                      data-testid="offers-submit-btn"
                    >
                      GET A FREE QUOTE
                    </button>

                    <p className="text-white/50 text-xs text-center pt-1">Free consultation &middot; No obligation &middot; Fast response</p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === All sections below are identical to Homepage === */}

      {/* Why Choose Us */}
      <section className="py-24 border-t border-gray-200" style={{ backgroundColor: '#1C2541' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Why Choose Alpha Epoxy?</h2>
            <p className="text-xl text-gray-300">Sydney's most trusted name in premium epoxy flooring</p>
          </div>
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            {/* Worker Image */}
            <div className="lg:col-span-2 flex justify-center items-center">
              <img
                src="/images/tradie-v2.png"
                alt="Alpha Epoxy professional"
                className="object-contain"
                style={{ maxHeight: '120%', width: '128%', maxWidth: '128%' }}
                loading="lazy"
              />
            </div>
            {/* Feature Cards */}
            <div className="lg:col-span-3 grid sm:grid-cols-2 gap-6">
              {mockData.features.map((feature, index) => (
                <Card key={index} className="border-2 border-red-600/30 shadow-lg hover:shadow-red-700/30 transition-all duration-300 hover:-translate-y-2 bg-white/5 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-4 transform hover:rotate-12 transition-transform duration-300">
                      {React.cloneElement(feature.icon, { className: "w-7 h-7 text-white" })}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-300 leading-relaxed text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-24 bg-gray-50" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Sydney-wide Epoxy Flooring Services</h2><p className="text-xl text-gray-600 max-w-3xl mx-auto">From North Shore to Southern Sydney, Eastern Suburbs to Western Sydney — we bring premium epoxy flooring solutions to your doorstep</p></div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-red-600 h-[500px]"><iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d424143.94532033163!2d150.65179054788284!3d-33.84714479524214!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b129838f39a743f%3A0x3017d681632a850!2sSydney%20NSW%2C%20Australia!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Sydney Service Area Map"></iframe></div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Coverage Across Greater Sydney</h3>
              <p className="text-gray-600 text-lg mb-8">No matter where you are in Sydney, our expert team is ready to transform your space with premium epoxy flooring solutions.</p>
              <div className="grid grid-cols-2 gap-4 mb-8">{["Northern Beaches","North Shore","Inner West","Eastern Suburbs","Western Sydney","Southern Sydney","Hills District","Sutherland Shire","Parramatta Area","Canterbury-Bankstown","Blacktown","Penrith"].map((area,i)=>(<div key={i} className="flex items-center"><CheckCircle2 className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" /><span className="text-gray-700 font-medium">{area}</span></div>))}</div>
              <Card className="border-2 border-red-600/50 bg-red-50"><CardContent className="p-6"><h4 className="text-xl font-bold text-gray-900 mb-3">Free On-Site Assessment</h4><p className="text-gray-700 mb-4">We'll visit your location anywhere in Sydney for a free, no-obligation consultation and provide an accurate quote on the spot.</p><Button className="bg-red-600 hover:bg-red-700 text-white font-bold" onClick={() => setIsModalOpen(true)}><PhoneCall className="w-4 h-4 mr-2" />Book Your Free Visit</Button></CardContent></Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Floating Quote Button */}
      {showFloatingButton && (
        <button onClick={() => setIsModalOpen(true)} className="fixed right-0 top-1/2 -translate-y-1/2 bg-red-600 hover:bg-red-700 text-white font-bold py-6 px-4 rounded-l-lg shadow-2xl z-50 transition-all duration-300 hover:px-6 animate-slideInRight" style={{ writingMode: 'vertical-rl' }}>
          <span className="flex items-center gap-2"><PhoneCall className="w-5 h-5" />GET A QUOTE NOW</span>
        </button>
      )}

      <QuoteModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} version="B" />
    </div>
  );
};

export default Offers;
